<template>
    <b-row cols="1" align-h="center" cols-lg="2">
        <b-col>
            <b-form-group
                label="Data de observação"
                label-for="observation-date-picker"
                invalid-feedback="Informe uma data"
                :state="observationDate.isValid"
            >
                <simbar-date-picker
                    id="observation-date-picker"
                    :value="observationDate.value"
                    :state="observationDate.isValid"
                    :disabled="
                        loadingReservoirInfos ||
                        config.status === Status.running
                    "
                    @input="onChangeObserversationDate"
                />
            </b-form-group>
            <div v-if="loadingReservoirInfos" class="text-center">
                <b-spinner
                    label="Spinning"
                    class="align-middle text-muted"
                /><span class="ml-2 align-middle text-muted"
                    >Carregando dados de Nível Reservatório, aguarde...</span
                >
            </div>
            <div v-else-if="observationDate.value !== ''">
                <b-nav
                    v-if="reservoirInfos.length > 0"
                    pills
                    align="left"
                    small
                    class="reservoir-info-times"
                >
                    <b-nav-item
                        v-for="item in reservoirInfos"
                        :key="item.epoch"
                        :active="
                            !!reservoirInfo &&
                            reservoirInfo.epoch === item.epoch
                        "
                        :disabled="config.status === Status.running"
                        @click="reservoirInfo = item"
                        >{{ item.time }}</b-nav-item
                    >
                </b-nav>
                <div v-else>
                    <span class="ml-2 align-middle text-muted"
                        >Nenhum dado de Nível Reservatório encontrado para este
                        dia.</span
                    >
                </div>
            </div>
            <template v-if="reservoirInfo !== null">
                <div class="mt-3">
                    <slot :reservoir-info="reservoirInfo" />
                </div>
                <div class="mt-3 text-right">
                    <b-button
                        :disabled="config.status === Status.running"
                        variant="secondary"
                        class="align-middle"
                        @click="onStartVisualizationGeneration"
                    >
                        <b-spinner
                            v-if="config.status === Status.running"
                            small
                            class="align-middle"
                        /><span
                            :class="{
                                'align-middle': true,
                                'ml-2': config.status === Status.running
                            }"
                            >{{
                                config.status === Status.running
                                    ? "Gerando Visualização 3D, aguarde..."
                                    : "Gerar Visualização 3D"
                            }}</span
                        >
                    </b-button>
                    <b-button
                        v-if="config.status === Status.running"
                        variant="danger"
                        class="ml-3"
                        :disabled="stoppingGeneration"
                        @click="onStopVisualizationGeneration"
                        >{{
                            stoppingGeneration ? "Parando, aguarde..." : "Parar"
                        }}</b-button
                    >
                    <b-modal
                        id="modal-confirm-generation"
                        ref="modalConfirmGeneration"
                        title="SIMBAR - Confirmação"
                        header-bg-variant="secondary"
                        header-text-variant="light"
                        footer-bg-variant="light"
                        cancel-title="Não"
                        cancel-variant="danger"
                        ok-title="Sim"
                        ok-variant="secondary"
                        centered
                        no-close-on-esc
                        no-close-on-backdrop
                        hide-header-close
                        no-fade
                        @ok="onConfirmStartVisualizationGeneration"
                    >
                        <template #default="{ visible }">
                            <template v-if="visible">
                                Deseja realmente gerar uma nova visualização?
                                <b-alert
                                    variant="warning"
                                    :show="true"
                                    class="mt-3"
                                >
                                    <strong>AVISO</strong> - Esta ação apagará a
                                    visualização anterior.
                                </b-alert>
                            </template>
                        </template>
                    </b-modal>
                </div>
                <div
                    v-if="
                        config.status === Status.success ||
                        config.status === Status.failed
                    "
                    class="mt-3"
                >
                    <b-alert
                        :variant="
                            config.status === Status.failed
                                ? 'danger'
                                : 'success'
                        "
                        :show="true"
                        class="simulation-alert"
                    >
                        <div class="icon">
                            <i
                                :class="{
                                    'fas fa-2x': true,
                                    'fa-exclamation-circle':
                                        config.status === Status.none,
                                    'fa-times-circle':
                                        config.status === Status.failed,
                                    'fa-check-circle':
                                        config.status === Status.success,
                                    'fa-spinner fa-pulse':
                                        config.status === Status.running
                                }"
                            />
                        </div>
                        <div class="message">
                            <div class="mb-3">
                                <template
                                    v-if="config.status === Status.success"
                                >
                                    Visualização gerada com sucesso.
                                    <b-button
                                        v-if="config.status === Status.success"
                                        variant="link"
                                        class="p-0 text-decoration-none"
                                        @click="$emit('open-visualization-tab')"
                                        >Abrir visualização.</b-button
                                    >
                                </template>
                                <template v-else>
                                    Um erro ocorreu durante a geração da
                                    visualização, confira o log.
                                </template>
                            </div>
                            <div>
                                Data de observação: {{ reservoirInfoEpoch }}
                            </div>
                        </div>
                    </b-alert>
                    <card-collapsable
                        ref="logCollapse"
                        title="Log da geração"
                        :variant="
                            config.status === Status.failed
                                ? 'danger'
                                : 'success'
                        "
                        :start-visible="config.status === Status.failed"
                    >
                        <b-form-textarea
                            :value="config.log"
                            rows="5"
                            max-rows="10"
                            readonly
                        />
                    </card-collapsable>
                </div>
            </template>
        </b-col>
    </b-row>
</template>

<script>
import moment from "moment";
import { Status } from "@/modules/simulator/store";
import api from "@/modules/common/api";
import sleep from "@/utils/sleep";
import CardCollapsable from "@/components/CardCollapsable.vue";
import SimbarDatePicker from "@/components/SimbarDatePicker.vue";

export default {
    name: "InputParameters",
    components: {
        SimbarDatePicker,
        CardCollapsable
    },
    data() {
        return {
            observationDate: {
                isValid: null,
                value: ""
            },
            reservoirInfos: [],
            loadingReservoirInfos: false,
            stoppingGeneration: false,
            reservoirInfo: null
        };
    },
    computed: {
        source() {
            return this.$store.state.common.source;
        },
        config() {
            return this.$store.state.common.visualization;
        },
        Status() {
            return Status;
        },
        reservoirInfoEpoch() {
            return this.$store.getters[
                "common/visualization/reservoirInfoEpoch"
            ];
        }
    },
    watch: {
        "config.status"() {
            this.stoppingGeneration = false;
        }
    },
    async mounted() {
        if (this.config.reservoirInfo) {
            this.observationDate.value = moment(
                this.config.reservoirInfo.epoch
            ).format("YYYY-MM-DD");
            await this.updateReservoirInfos();
            this.reservoirInfo = this.reservoirInfos.find(
                item => item.epoch === this.config.reservoirInfo.epoch
            );
        }
    },
    methods: {
        onChangeObserversationDate(observationDate) {
            this.observationDate.isValid =
                observationDate === "" ? false : null;
            this.observationDate.value = observationDate;
            this.reservoirInfo = null;

            if (
                this.observationDate.isValid === null &&
                this.observationDate.value !== ""
            ) {
                this.updateReservoirInfos();
            }
        },
        onStartVisualizationGeneration() {
            if (this.config.status === this.Status.success) {
                this.$refs.modalConfirmGeneration.show();
            } else {
                this.onConfirmStartVisualizationGeneration();
            }
        },
        onConfirmStartVisualizationGeneration() {
            this.$store.dispatch(
                "common/visualization/startVisualizationGeneration",
                {
                    epoch: this.reservoirInfo.epoch
                }
            );
        },
        onStopVisualizationGeneration() {
            this.stoppingGeneration = true;
            this.$store.dispatch(
                "common/visualization/stopVisualizationGeneration"
            );
        },
        async updateReservoirInfos() {
            this.loadingReservoirInfos = true;

            const { data } = await api.getReservoirInfosByDay(
                this.source,
                this.observationDate.value
            );
            await sleep(500);

            this.reservoirInfos = data.map(item => {
                item.time = moment(item.epoch).format("HH:mm");
                return item;
            });

            this.loadingReservoirInfos = false;
        }
    }
};
</script>
<style lang="scss" scoped>
.reservoir-info-times .nav-link {
    color: $body-color;
    &.active {
        color: $white;
        background-color: $secondary;
    }
}
</style>
